/* eslint-disable max-len */
export const clientRoutes = () => ({
  clients: '/clients',
  clientsCreate: '/clients/:clientId',
  companyInfo: '/clients/:clientId/company-info',
  multiLingual: '/clients/:clientId/multi-lingual',
  companyAdmin: '/clients/:clientId/company-admin',
  uiConfiguration: '/clients/:clientId/ui-configuration',
  moduleManagement: '/clients/:clientId/module-management',
  security: '/clients/:clientId/security',
  companyProfile: '/clients/:clientId/company-profile',
  clientProfile: '/clients/:clientId/info',
  clientUsers: '/clients/:clientId/info/users',
  clientUsersSecurity: '/clients/:clientId/info/users/:userId',
});

/**
 * A new route is unknown to the AI chat until it is listed in `AI_SCREEN_CONTEXT_ROUTES`. Until
 * then the assistant reads the path as the screen id.
 * @see [12.5 Register a screen on the frontend](../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
export const AppRoutes = {
  home: '/',
  login: '/login',
  authenticationType: '/login/auth',
  authenticationSetup: '/login/auth/app/setup/:type?/:step?',
  verificationSmsSetup: '/login/auth/sms/setup',
  verificationSms: '/login/auth/sms',
  verificationCode: '/login/auth/app/code',
  recoverPassword: '/login/recover-password',
  recoverPasswordSuccess: '/login/recover-password/success',
  recoverPasswordConfirm: '/login/recover-password/confirm',
  newPassword: '/login/new-password',
  deactivated: '/login/auth/deactivated',
  profile: '/profile',
  personalInfo: '/profile/personal-info',
  profilePassword: '/profile/password',
  profileLanguage: '/profile/language',
  profileSecurity: '/profile/security/:type?/:step?',
  launchpad: '/launchpad',
  launchpadHub: '/value-chain-launchpad',
  launchpadSettings: '/launchpad/settings',
  releaseHistory: '/launchpad/release-history',
  userManagement: '/launchpad/settings/user-management',
  companyUiConfiguration: '/launchpad/settings/ui-configuration',
  createNewUser: '/launchpad/settings/user-management/create-new-user',
  createUserPermissions: '/launchpad/settings/user-management/:userId/create-user-permissions',
  userInfo: '/launchpad/settings/user-management/:userId',
  editUserInfo: '/launchpad/settings/user-management/:userId/edit-user-info',
  editUserPermissions: '/launchpad/settings/user-management/:userId/edit-user-permissions',
  balanceSheetAssets: '/launchpad/settings/data-sets-management/balance-sheet-assets',
  balanceSheetAssetsCreate: '/launchpad/settings/data-sets-management/balance-sheet-assets/create',
  balanceSheetAssetsEdit: '/launchpad/settings/data-sets-management/balance-sheet-assets/edit/:recordId',
  suppliers: '/launchpad/settings/data-sets-management/suppliers',
  customers: '/launchpad/settings/data-sets-management/customers',
  products: '/launchpad/settings/data-sets-management/products',
  profitCentres: '/launchpad/settings/data-sets-management/profit-centres',
  profitCentresCreate: '/launchpad/settings/data-sets-management/profit-centres/create',
  profitCentresEdit: '/launchpad/settings/data-sets-management/profit-centres/edit/:recordId',
  supplierCreate: '/launchpad/settings/data-sets-management/suppliers/create',
  supplierEdit: '/launchpad/settings/data-sets-management/suppliers/edit/:recordId',
  productsCreate: '/launchpad/settings/data-sets-management/products/create',
  productsEdit: '/launchpad/settings/data-sets-management/products/edit/:recordId',
  customerCreate: '/launchpad/settings/data-sets-management/customers/create',
  customerEdit: '/launchpad/settings/data-sets-management/customers/edit/:recordId',
  valueChainMap: '/value-chain-map',
  baseMultilingual: '/base-multilingual',
  companyMap: '/company-map/:valueChainMapId',
  taskManagement: '/task-management',
  taskManagementId: '/task-management/:taskId',
  materialityAssessment: '/materiality-assessment',
  materialityAssessmentInitiate: '/materiality-assessment/initiate/:recordId?',
  materialityAssessmentStakeholdersURL: '/materiality-assessment/:recordId',
  materialityAssessmentStakeholders: '/materiality-assessment/:recordId/stakeholderID',

  // stakeholder
  materialityAssessmentSupplier: '/materiality-assessment/:recordId/suppliers/:vcmId',
  materialityAssessmentInvestors: '/materiality-assessment/:recordId/investors/:vcmId',
  materialityAssessmentBoards: '/materiality-assessment/:recordId/boards/:vcmId',
  materialityAssessmentEmployees: '/materiality-assessment/:recordId/employees/:vcmId',
  materialityAssessmentCustomers: '/materiality-assessment/:recordId/customers/:vcmId',
  materialityAssessmentLocalCommunity: '/materiality-assessment/:recordId/local_communities/:vcmId',
  materialityAssessmentLocalMedia: '/materiality-assessment/:recordId/media/:vcmId',
  materialityAssessmentLocalNGOs: '/materiality-assessment/:recordId/ngos/:vcmId',

  materialityAssessmentSurveyCreate: '/materiality-assessment/:recordId/survey/create',
  materialityAssessmentSurveyEdit: '/materiality-assessment/:recordId/survey/:surveyId/edit',
  materialityAssessmentSurveyAnswer: '/insight-create',
  materialityAssessmentSurveyAnswerPrioritise: '/insight-create/prioritise/:recordId',
  materialityAssessmentSurveyCreation: '/materiality-assessment/:recordId/survey-creation',
  materialityAssessmentSurvey: '/materiality-assessment/:recordId/survey',
  materialityAssessmentInterview: '/materiality-assessment/:recordId/interview',
  materialityAssessmentRoundtables: '/materiality-assessment/:recordId/roundtables',
  materialityAssessmentInsightsDashboard: '/materiality-assessment/:recordId/dashboard',
  materialityAssessmentTopicIdentification: '/materiality-assessment/:recordId/topicIdentification',

  // topic refinement 1
  materialityAssessmentInvestorsCustomersEvidence: '/materiality-assessment/:recordId/investors-customers-evidence/:vcmId',
  materialityAssessmentNonRegulatory: '/materiality-assessment/:recordId/non-sustainability-regulatory-disclosures/:vcmId',
  materialityAssessmentCompetitor: '/materiality-assessment/:recordId/competitor/:vcmId',
  materialityAssessmentAnalysis: '/materiality-assessment/:recordId/analysis/:vcmId',
  materialityAssessmentGeographic: '/materiality-assessment/:recordId/geographic/:vcmId',
  materialityAssessmentIndustry: '/materiality-assessment/:recordId/industry/:vcmId',

  standards: '/standards',
  sectorSpecific: '/sector-specific',
  standardsIRO: '/standards-iro',
  materialityAssessmentStakeholdersDefault: '/materiality-assessment/:recordId/:stakeholderId',
  materialityAssessmentIROIdentification: '/materiality-assessment/:recordId/iroIdentification',
  materialityAssessmentIROScoring: '/materiality-assessment/:recordId/iroScoring',
  materialityAssessmentIROQuestions: '/questionnaire-create',
  materialityAssessmentTopicRefinement: '/materiality-assessment/:recordId/topicRefinement',
  materialityAssessmentTopicRefinementII: '/materiality-assessment/:recordId/topicRefinementII',
  materialityAssessmentRespondentSurveys: '/materiality-assessment/:recordId/respondentSurveys/:surveyId/:vcmId?',
  materialityAssessmentIRORefinement: '/materiality-assessment/:recordId/iroRefinement',

  settingWeighting: '/setting-weighting',
  agentsManagement: '/agents-management',
  watchTower: '/agents-management/watch-tower',
  watchTowerCompany: '/agents-management/watch-tower/companies/:id',
  watchTowerImport: '/agents-management/watch-tower/import',
  watchTowerSchedules: '/agents-management/watch-tower/schedules',
  watchTowerRuns: '/agents-management/watch-tower/runs',
  watchTowerTopics: '/agents-management/watch-tower/topics',
  watchTowerTopic: '/agents-management/watch-tower/topics/:id',
  marketingOps: '/agents-management/marketing-ops',
  marketingOpsFinding: '/agents-management/marketing-ops/findings/:publicId',
  ...clientRoutes(),
} as const;

export type TAppRoutes = typeof AppRoutes[keyof typeof AppRoutes];

export default AppRoutes;
